/**
 * Utility functions for building O365 app URLs with workspace context
 *
 * These functions add authentication hints and tenant context parameters for
 * B2B guest users to access resources in managed tenants.
 *
 * URL Strategy by App (based on Microsoft documentation):
 * - Teams: tenantId parameter with tenant GUID
 * - Outlook: outlook.com/{domain} format (NOT outlook.office.com)
 * - SharePoint/OneDrive: Tenant-specific URLs (already tenant-scoped)
 * - Office apps: ?whr= parameter for home realm discovery
 *
 * References:
 * - https://blog.darrenjrobinson.com/azure-ad-and-microsoft-office365-deep-links-and-sign-in-urls/
 * - https://learn.microsoft.com/en-us/microsoftteams/platform/concepts/build-and-test/deep-link-teams
 */

/**
 * Workspace context for building app URLs with authentication hints
 */
export interface WorkspaceContext {
  /** Workspace ID (tenant ID GUID) */
  workspaceId: string;
  /** Workspace display name */
  workspaceName: string;
  /** Managed tenant domain (e.g., contoso.onmicrosoft.com) */
  domain: string;
}

/**
 * Compute B2B guest UPN for a user in a managed tenant.
 *
 * Azure B2B creates guest accounts with a predictable UPN format:
 *   {original UPN with @ -> _}#EXT#@{managed tenant domain}
 *
 * Example:
 *   Home UPN: john.doe@macgregor.consulting
 *   Managed domain: contoso.onmicrosoft.com
 *   B2B Guest UPN: john.doe_macgregor.consulting#EXT#@contoso.onmicrosoft.com
 *
 * @param homeUpn - User's UPN in their home tenant
 * @param managedTenantDomain - Domain of the managed tenant
 * @returns B2B guest UPN for the managed tenant
 */
export function computeB2BGuestUpn(homeUpn: string, managedTenantDomain: string): string {
  // Replace @ with _ in the home UPN
  const escapedUpn = homeUpn.replace('@', '_');
  // Construct B2B guest UPN
  return `${escapedUpn}#EXT#@${managedTenantDomain}`;
}

/**
 * Add workspace context params for browser extension tab grouping.
 */
function addWorkspaceContextParams(url: URL, workspace: WorkspaceContext): void {
  url.searchParams.set('wsm_workspace_id', workspace.workspaceId);
  url.searchParams.set('wsm_workspace_name', workspace.workspaceName);
  url.searchParams.set('wsm_tenant_id', workspace.workspaceId);
}

/**
 * Build Teams URL with tenantId parameter.
 *
 * Teams requires the tenantId (GUID) parameter to switch tenant context for B2B users.
 * Format: https://teams.microsoft.com/?tenantId={tenant-guid}
 */
export function buildTeamsUrl(workspace: WorkspaceContext): string {
  const url = new URL('https://teams.microsoft.com/');
  url.searchParams.set('tenantId', workspace.workspaceId);
  addWorkspaceContextParams(url, workspace);
  return url.toString();
}

/**
 * Build Teams web URL for a specific tenant.
 *
 * Opens Teams web with the correct tenant context. Users navigate to their
 * specific team from there. This avoids deep linking which triggers the
 * desktop app launcher.
 *
 * @param _teamId - The team's group ID (currently unused, kept for API compatibility)
 * @param workspace - Workspace context for tenant ID
 */
export function buildTeamsTeamUrl(_teamId: string, workspace: WorkspaceContext): string {
  return buildTeamsUrl(workspace);
}

/**
 * Build Outlook URL with tenant domain path.
 *
 * Outlook supports tenant-specific access via domain in path:
 * Format: https://outlook.com/{tenant-domain}
 *
 * Note: This is different from outlook.office.com which doesn't support this.
 */
export function buildOutlookUrl(workspace: WorkspaceContext): string {
  // Use outlook.com/{domain} format for tenant-specific access
  const url = new URL(`https://outlook.com/mail/${workspace.domain}`);
  addWorkspaceContextParams(url, workspace);
  return url.toString();
}

/**
 * Build Office app URL (Word, Excel, PowerPoint, OneNote) with whr parameter.
 *
 * Office apps use the whr (Windows Home Realm) parameter to hint which tenant to use.
 * Format: https://www.office.com/launch/{app}?whr={domain}
 */
export function buildOfficeAppUrl(appName: string, workspace: WorkspaceContext): string {
  const url = new URL(`https://www.office.com/launch/${appName}`);
  url.searchParams.set('whr', workspace.domain);
  addWorkspaceContextParams(url, workspace);
  return url.toString();
}

/**
 * Build SharePoint/OneDrive URL.
 *
 * These are already tenant-specific by URL structure:
 * - SharePoint: https://{tenant}.sharepoint.com
 * - OneDrive: https://{tenant}-my.sharepoint.com
 *
 * No additional auth parameters needed - the URL itself determines the tenant.
 */
export function buildSharePointUrl(baseUrl: string, workspace: WorkspaceContext): string {
  const url = new URL(baseUrl);
  addWorkspaceContextParams(url, workspace);
  return url.toString();
}

/**
 * Build app URL with appropriate tenant context based on app type.
 *
 * Each Microsoft 365 app has different requirements for tenant switching:
 * - Teams: tenantId query parameter
 * - Outlook: Domain in URL path
 * - Office apps: whr parameter
 * - SharePoint/OneDrive: Already tenant-specific by URL
 *
 * @param baseUrl - Base URL of the O365 app
 * @param workspace - Workspace context (id, name, domain)
 * @param _userUpn - User's UPN (currently unused but kept for API compatibility)
 * @returns Full URL with appropriate parameters for tenant context
 */
export function buildAppUrlWithContext(
  baseUrl: string,
  workspace: WorkspaceContext,
  _userUpn?: string
): string {
  // Determine app type from URL and use appropriate strategy
  const urlLower = baseUrl.toLowerCase();

  // Teams - use tenantId parameter
  if (urlLower.includes('teams.microsoft.com')) {
    return buildTeamsUrl(workspace);
  }

  // Outlook - use domain in path
  if (urlLower.includes('outlook.office.com') || urlLower.includes('outlook.com')) {
    return buildOutlookUrl(workspace);
  }

  // Office apps (Word, Excel, PowerPoint, OneNote) - use whr parameter
  if (urlLower.includes('office.com/launch/')) {
    const appMatch = urlLower.match(/office\.com\/launch\/(\w+)/);
    if (appMatch) {
      return buildOfficeAppUrl(appMatch[1], workspace);
    }
  }

  // SharePoint/OneDrive - already tenant-specific, just add workspace context
  if (urlLower.includes('.sharepoint.com')) {
    return buildSharePointUrl(baseUrl, workspace);
  }

  // Default fallback: add whr parameter for home realm discovery
  const url = new URL(baseUrl);
  url.searchParams.set('whr', workspace.domain);
  addWorkspaceContextParams(url, workspace);
  return url.toString();
}
